import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import API from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FolderOpen, Clock, CheckCircle, PoundSterling, ArrowRight, Trash2, X, Bell } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useCurrency } from "@/lib/currency";
import { HealthScoreBadge } from "@/components/HealthScoreBadge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { isPushSupported, subscribeToPush } from "@/lib/pushNotifications";

const STATUS_COLORS = {
  Draft: "bg-slate-800 text-slate-300 border-slate-700",
  "Ready for Review": "bg-blue-500/10 text-blue-400 border-blue-500/20",
  "Under Review": "bg-amber-500/10 text-amber-400 border-amber-500/20",
  "Revision Required": "bg-orange-500/10 text-orange-400 border-orange-500/20",
  Approved: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  Rejected: "bg-rose-500/10 text-rose-400 border-rose-500/20",
  Issued: "bg-sky-500/10 text-sky-400 border-sky-500/20",
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isDirector } = useAuth();
  const cur = useCurrency();
  const confirm = useConfirm();
  const [showPushBanner, setShowPushBanner] = useState(false);

  useEffect(() => {
    API.get("/dashboard")
      .then((r) => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
    // Check if we should show push notification prompt
    if (isPushSupported() && typeof Notification !== "undefined" && Notification.permission === "default") {
      setShowPushBanner(true);
    }
  }, []);

  const handleEnablePush = useCallback(async () => {
    try {
      const sub = await subscribeToPush();
      if (sub) {
        toast.success("Push notifications enabled");
        setShowPushBanner(false);
      } else {
        toast.error("Could not enable notifications. Please check your browser settings.");
      }
    } catch {
      toast.error("Failed to enable notifications");
    }
  }, []);

  useEffect(() => {
    API.get("/dashboard")
      .then((r) => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <DashboardSkeleton />;

  const kpis = [
    { label: "Total Projects", value: data?.total_projects || 0, icon: FolderOpen, color: "text-blue-500", bg: "bg-blue-500/10" },
    { label: "In Progress", value: data?.in_progress || 0, icon: Clock, color: "text-amber-500", bg: "bg-amber-500/10" },
    { label: "Awaiting Approval", value: data?.awaiting_approval || 0, icon: CheckCircle, color: "text-sky-500", bg: "bg-sky-500/10" },
    { label: "Total Fees", value: `${cur.symbol}${(data?.total_fees || 0).toLocaleString("en-GB", { minimumFractionDigits: 0 })}`, icon: PoundSterling, color: "text-emerald-500", bg: "bg-emerald-500/10" },
  ];

  return (
    <div data-testid="dashboard" className="space-y-8">
      {showPushBanner && (
        <div className="flex items-center gap-3 bg-blue-500/10 border border-blue-500/20 rounded-lg px-4 py-3" data-testid="push-notification-banner">
          <Bell className="w-4 h-4 text-blue-400 shrink-0" />
          <p className="text-sm text-blue-300 flex-1">Enable push notifications to stay updated on approvals, timesheets, and pricing requests.</p>
          <Button size="sm" variant="outline" className="text-xs border-blue-500/30 text-blue-400 hover:bg-blue-500/20" onClick={handleEnablePush} data-testid="enable-push-btn">Enable</Button>
          <button className="text-slate-500 hover:text-slate-300" onClick={() => setShowPushBanner(false)}><X className="w-3.5 h-3.5" /></button>
        </div>
      )}
      <div>
        <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">MEP Pricing Engine overview</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 stagger-children dashboard-kpi-grid">
        {kpis.map((kpi) => (
          <Card key={kpi.label} className="bg-slate-900 border-slate-800 hover:border-slate-700 transition-colors" data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s/g, "-")}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-heading font-medium uppercase tracking-wider text-slate-500">{kpi.label}</p>
                  <p className="font-mono text-3xl font-bold mt-1 text-slate-100">{kpi.value}</p>
                </div>
                <div className={`w-10 h-10 rounded-lg ${kpi.bg} flex items-center justify-center`}>
                  <kpi.icon className={`w-5 h-5 ${kpi.color}`} strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        <Card className="lg:col-span-2 bg-slate-900 border-slate-800" data-testid="recent-projects">
          <CardHeader className="border-b border-slate-800 py-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-base font-semibold text-slate-100">Recent Projects</CardTitle>
              <Link to="/projects" className="text-xs font-medium text-blue-500 hover:text-blue-400 flex items-center gap-1 transition-colors" data-testid="view-all-projects">
                View all <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="table-scroll-wrapper">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider font-medium text-slate-500 px-4">Project</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-medium text-slate-500 px-4">Client</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-medium text-slate-500 px-4">Status</TableHead>
                  {isDirector && <TableHead className="text-xs uppercase tracking-wider font-medium text-slate-500 px-4">Health</TableHead>}
                  <TableHead className="text-xs uppercase tracking-wider font-medium text-slate-500 px-4">Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.recent_projects?.length > 0 ? (
                  data.recent_projects.slice(0, 8).map((p) => (
                    <TableRow key={p.id} className="border-slate-800 hover:bg-slate-800/50 cursor-pointer" data-testid={`project-row-${p.id}`}>
                      <TableCell className="px-4 py-3">
                        <Link to={`/projects/${p.id}`} className="text-sm font-medium text-slate-200 hover:text-blue-400 transition-colors">{p.name}</Link>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-sm text-slate-400">{p.client_name}</TableCell>
                      <TableCell className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[p.status] || STATUS_COLORS.Draft}`}>{p.status}</span>
                      </TableCell>
                      {isDirector && (
                        <TableCell className="px-4 py-3">
                          {p.health_score && p.health_score.color !== "gray" ? (
                            <HealthScoreBadge healthScore={p.health_score} size="compact" />
                          ) : (
                            <span className="text-xs text-slate-600">—</span>
                          )}
                        </TableCell>
                      )}
                      <TableCell className="px-4 py-3 font-mono text-xs text-slate-500">{new Date(p.updated_at).toLocaleDateString("en-GB")}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow className="border-slate-800">
                    <TableCell colSpan={isDirector ? 5 : 4} className="text-center py-8 text-sm text-slate-500">
                      No projects yet.{" "}
                      <Link to="/projects/new" className="text-blue-500 hover:text-blue-400 font-medium" data-testid="create-first-project">Create your first project</Link>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader className="border-b border-border py-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-base font-semibold text-foreground">Recent Activity</CardTitle>
              {isDirector && data?.recent_activity?.length > 0 && (
                <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground hover:text-destructive"
                  data-testid="clear-all-activity-btn"
                  onClick={async () => {
                    if (!(await confirm({ title: "Clear Activity Log", description: "Clear all activity logs?", confirmText: "Clear All", variant: "warning" }))) return;
                    try {
                      await API.delete("/dashboard/activity");
                      setData(d => ({ ...d, recent_activity: [] }));
                      toast.success("Activity cleared");
                    } catch { toast.error("Failed to clear"); }
                  }}>
                  <Trash2 className="w-3 h-3 mr-1" />Clear All
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-4 max-h-96 overflow-y-auto">
            {data?.recent_activity?.length > 0 ? (
              <div className="space-y-3">
                {data.recent_activity.slice(0, 12).map((a, i) => (
                  <div key={a.id || i} className="flex gap-3 items-start group" data-testid={`activity-item-${i}`}>
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground/80">{a.description}</p>
                      <p className="font-mono text-xs text-muted-foreground mt-0.5">{new Date(a.timestamp).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                    </div>
                    {isDirector && a.id && (
                      <button
                        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-all"
                        data-testid={`delete-activity-${i}`}
                        onClick={async () => {
                          try {
                            await API.delete(`/dashboard/activity/${a.id}`);
                            setData(d => ({ ...d, recent_activity: d.recent_activity.filter(x => x.id !== a.id) }));
                          } catch { toast.error("Failed to delete"); }
                        }}>
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">No activity yet</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-8">
      <div>
        <div className="h-10 w-48 bg-slate-800 rounded animate-pulse" />
        <div className="h-4 w-64 bg-slate-800/50 rounded animate-pulse mt-2" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-24 bg-slate-900 border border-slate-800 rounded-lg animate-pulse" />
        ))}
      </div>
      <div className="h-64 bg-slate-900 border border-slate-800 rounded-lg animate-pulse" />
    </div>
  );
}
